import { useState, useEffect, useCallback } from "react";
import Layout from "../components/Layout";
import { API, useAuth } from "../App";
import axios from "axios";
import {
  Activity, Database, MessageSquare, CreditCard,
  CheckCircle2, XCircle, AlertTriangle, Clock,
  RefreshCw, Server, Wifi, Shield
} from "lucide-react";

const SERVICE_CONFIG = {
  mongodb: { label: "MongoDB", icon: Database, description: "Base de données" },
  notifications: { label: "Notifications", icon: MessageSquare, description: "Email / SMS / WhatsApp" },
  infobip: { label: "Infobip (Legacy)", icon: MessageSquare, description: "SMS / WhatsApp / Email" },
  stripe: { label: "Stripe", icon: CreditCard, description: "Paiements" },
};

const STATUS_MAP = {
  operational: { label: "Opérationnel", color: "text-emerald-400", bg: "bg-emerald-500/20", border: "border-emerald-500/30", dot: "bg-emerald-400", icon: CheckCircle2 },
  degraded: { label: "Dégradé", color: "text-amber-400", bg: "bg-amber-500/100/20", border: "border-amber-500/30", dot: "bg-amber-400", icon: AlertTriangle },
  down: { label: "Hors service", color: "text-red-400", bg: "bg-red-500/100/20", border: "border-red-500/30", dot: "bg-red-400", icon: XCircle },
  not_configured: { label: "Non configuré", color: "text-slate-400", bg: "bg-white/50/20", border: "border-slate-500/30", dot: "bg-slate-400", icon: AlertTriangle },
  auth_error: { label: "Erreur d'auth", color: "text-red-400", bg: "bg-red-500/100/20", border: "border-red-500/30", dot: "bg-red-400", icon: XCircle },
  partial_outage: { label: "Panne partielle", color: "text-amber-400", bg: "bg-amber-500/100/20", border: "border-amber-500/30", dot: "bg-amber-400", icon: AlertTriangle },
};

const getStatus = (key) => STATUS_MAP[key] || STATUS_MAP.down;

export default function SystemStatusPage() {
  const { token } = useAuth();
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchHealth = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/system/health`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHealth(res.data);
      setLastRefresh(new Date());
    } catch {
      setHealth(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchHealth();
  }, [fetchHealth]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchHealth, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchHealth]);

  const overallStatus = health ? getStatus(health.overall_status) : getStatus("down");
  const OverallIcon = overallStatus.icon;

  return (
    <Layout>
      <div className="p-4 md:p-8 max-w-5xl mx-auto" data-testid="system-status-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white" data-testid="status-title">
              Statut du Système
            </h1>
            <p className="text-slate-500 mt-1">Surveillance en temps réel des services</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                autoRefresh
                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                  : "bg-white/5 text-slate-500 border-white/10"
              }`}
              data-testid="auto-refresh-toggle"
            >
              {autoRefresh ? "Auto: ON (30s)" : "Auto: OFF"}
            </button>
            <button
              onClick={fetchHealth}
              disabled={loading}
              className="p-2 rounded-lg bg-sky-50 text-sky-600 hover:bg-sky-100 border border-sky-200 transition-all disabled:opacity-50"
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>

        {/* Overall Status Banner */}
        <div
          className={`mb-8 rounded-xl border-2 p-6 ${overallStatus.border} ${overallStatus.bg}`}
          data-testid="overall-status"
        >
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${overallStatus.bg}`}>
              <OverallIcon className={`w-6 h-6 ${overallStatus.color}`} />
            </div>
            <div>
              <h2 className={`text-lg font-bold ${overallStatus.color}`} data-testid="overall-status-label">
                {health ? (health.overall_status === "operational" ? "Tous les systèmes sont opérationnels" : "Certains services nécessitent une attention") : "Vérification en cours..."}
              </h2>
              <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                {health && (
                  <>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" /> Uptime: {health.uptime}
                    </span>
                    <span className="flex items-center gap-1">
                      <Server className="w-3 h-3" /> v{health.version}
                    </span>
                    <span className="flex items-center gap-1">
                      <Shield className="w-3 h-3" /> {health.environment === "test" ? "Mode Test" : "Production"}
                    </span>
                  </>
                )}
                {lastRefresh && (
                  <span className="flex items-center gap-1">
                    <Wifi className="w-3 h-3" />
                    {lastRefresh.toLocaleTimeString("fr-FR")}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Services Grid */}
        <div className="grid gap-4 md:grid-cols-3 mb-8" data-testid="services-grid">
          {health && Object.entries(health.services).map(([key, service]) => {
            const config = SERVICE_CONFIG[key] || { label: key, icon: Server, description: key };
            const status = getStatus(service.status);
            const StatusIcon = status.icon;
            const ServiceIcon = config.icon;

            return (
              <div
                key={key}
                className={`rounded-xl border p-5 bg-white shadow-sm transition-all hover:shadow-md ${status.border}`}
                data-testid={`service-${key}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${status.bg}`}>
                      <ServiceIcon className={`w-5 h-5 ${status.color}`} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white">{config.label}</h3>
                      <p className="text-xs text-slate-400">{config.description}</p>
                    </div>
                  </div>
                  <StatusIcon className={`w-5 h-5 ${status.color}`} />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-500">Statut</span>
                    <span className={`text-xs font-semibold ${status.color} flex items-center gap-1`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${status.dot} animate-pulse`} />
                      {status.label}
                    </span>
                  </div>

                  {service.latency_ms >= 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-500">Latence</span>
                      <span className={`text-xs font-mono ${service.latency_ms < 200 ? "text-emerald-600" : service.latency_ms < 500 ? "text-amber-400" : "text-red-400"}`}>
                        {service.latency_ms} ms
                      </span>
                    </div>
                  )}

                  {service.mode && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-500">Mode</span>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${service.mode === "live" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-400"}`}>
                        {service.mode === "live" ? "Production" : "Test"}
                      </span>
                    </div>
                  )}

                  {service.channels && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-500">Canaux</span>
                      <div className="flex gap-1">
                        {service.channels.map(ch => (
                          <span key={ch} className="text-xs bg-sky-50 text-sky-700 px-1.5 py-0.5 rounded font-medium">
                            {ch}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {key === "mongodb" && service.stats && (
                    <div className="pt-2 border-t border-white/5 mt-2 grid grid-cols-3 gap-2">
                      {Object.entries(service.stats).map(([k, v]) => (
                        <div key={k} className="text-center">
                          <span className="text-lg font-bold text-white">{v}</span>
                          <p className="text-[10px] text-slate-400 capitalize">{k}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {service.error && (
                    <div className="mt-2 p-2 rounded bg-red-500/10 border border-red-100">
                      <p className="text-xs text-red-400 font-mono break-all">{service.error}</p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* API Endpoint Info */}
        <div className="bg-white rounded-xl border border-white/10 p-5 shadow-sm" data-testid="api-info">
          <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
            <Activity className="w-4 h-4 text-sky-500" />
            Point d'accès API
          </h3>
          <div className="space-y-2">
            <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-white/5 border border-white/5">
              <span className="text-sm text-slate-600">Health Check</span>
              <code className="text-xs text-sky-600 font-mono">GET /api/system/health</code>
            </div>
            <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-white/5 border border-white/5">
              <span className="text-sm text-slate-600">Basic Health</span>
              <code className="text-xs text-sky-600 font-mono">GET /api/health</code>
            </div>
          </div>
          <p className="text-xs text-slate-400 mt-3">
            Rafraîchissement automatique toutes les 30 secondes. Accès réservé aux administrateurs.
          </p>
        </div>
      </div>
    </Layout>
  );
}
